import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn, tailwind } from "@/utilities/tailwind-utils";
import {
  ActiveIcon,
  CheckBoldIcon,
  CrossBoldIcon,
  EditDraftBoldIcon,
  UrgentIcon,
} from "@/utilities/icon-utils";

const badgeVariants = cva(tailwind.components.badge, {
  variants: {
    variant: {
      default: tailwind.components.badgeDefault,
      secondary: tailwind.components.badgeSecondary,
      destructive: tailwind.components.badgeDestructive,
      outline: tailwind.components.badgeOutline,
      active: "bg-success/7 text-success",
      upcoming: "bg-error/7 text-error",
      completed: "bg-blue/7 text-blue",
      cancelled: "bg-warning/7 text-warning",
      drafted: "",
    },
  },
  defaultVariants: {
    variant: "active",
  },
});

const icons = {
  active: ActiveIcon,
  upcoming: UrgentIcon,
  completed: CheckBoldIcon,
  cancelled: CrossBoldIcon,
  drafted: EditDraftBoldIcon,
};

function Badge({
  className,
  variant,
  asChild = false,
  hideIcon = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & {
    asChild?: boolean;
    hideIcon?: boolean;
  }) {
  const Comp = asChild ? Slot : "span";
  const IconComponent =
    !hideIcon && variant && (variant as keyof typeof icons) in icons
      ? icons[variant as keyof typeof icons]
      : null;

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    >
      {IconComponent && <IconComponent />}
      {props.children}
    </Comp>
  );
}

export type BadgeVariants = VariantProps<typeof badgeVariants>;
export type BadgeVariant = BadgeVariants["variant"];

export { Badge, badgeVariants };
